import React, {Component, Fragment} from 'react'
import PropTypes from 'prop-types'
import StyleSheet from 'styles'
import moment from 'moment'
import Typography from '@material-ui/core/Typography'
import Table, { TableCell, TableBody, TableHead, TableRow, styles as tableStyles} from "components/CollapsibleTable";
import Button from '@material-ui/core/Button'
import {DateTimeDisplay, Markdown} from 'components'
import {NumberDisplay} from 'components/numbers'
import { withRouter } from 'react-router-dom'
import {FormattedMessage, injectIntl} from 'react-intl'

export class AllocationDetails extends Component {
  static propTypes = {
    dealSheet: PropTypes.object.isRequired,
    allocations: PropTypes.array.isRequired,
    nominations: PropTypes.array.isRequired,
    transactions: PropTypes.array.isRequired,
  }

  get totalAllocationSize() {
    return `${this.props.allocations.reduce((total, allocation) => total + Number(allocation.amount), 0)} ${this.ticker}`
  }

  get allocationFilled() {
    const value = this.props.allocations.reduce((total, allocation) => total + Number(allocation.amountClaimed), 0)
    return value > 0 ? `${value} ${this.ticker}` : ''
  }

  get pendingAllocationSize() {
    const valueNominations = this.props.nominations.filter(({value}) => value)
    const amountNominations = this.props.nominations.filter(({amount}) => amount)
    return [valueNominations, amountNominations].map(nominations => {
      const size = nominations.reduce((total, nomination) => total + Number(nomination.amount || nomination.value), 0)
      const unit = nominations === valueNominations ? this.props.dealSheet.currency.code : (this.props.dealSheet.ticker || 'Unit')
      return size > 0 ? `${size} ${unit}` : ''
    }).filter(n => n).join(' + ')
  }

  get ticker() {
    return this.props.dealSheet.typeOfIssuance === 'equity'
      ? this.props.dealSheet.currency.code
      : this.props.dealSheet.ticker || 'Unit';
  }

  get dealSheetState() {
    const {nominations} = this.props
    if (this.allocationsIncludesState('incomplete'))
      return 'incomplete'
    else if (this.allocationsIncludesState('saft_pending'))
      return 'saft_pending'
    if (this.allocationsIncludesState('upcoming'))
      return 'upcoming'
    else if (nominations.length > 0)
      return 'pending'
    else if (this.allocationsIncludesState('complete'))
      return 'complete'
    else if (this.allocationsIncludesState('closed'))
      return 'closed'
    else
      return 'none'
  }

  allocationsIncludesState = state => {
    return this.props.allocations.some(allocation => this.allocationState(allocation) === state)
  }

  allocationState = allocation => {
    if (!allocation.saftComplete)
      return 'saft_pending'
    else if (moment(allocation.startDate) > moment(Date.now()))
      return 'upcoming'
    else if (moment(allocation.endDate) < moment(Date.now()))
      return 'closed'
    else if (allocation.paymentRemaining > 0)
      return 'incomplete'
    else
      return 'complete'
  }

  handleContribute = allocation => () => {
    this.props.history.push(`/deal_sheets/${this.props.dealSheet.id}/allocations/${allocation.id}`)
  }


  renderStatus = () => {
    const {intl} = this.props
    return (
      <Markdown mdtext={intl.formatMessage({id: `deal.allocation_status.${this.dealSheetState}.description`})}
                allowedTags={['strong']} noStyling />
    )
  }

  render = () => {
    const {dealSheet, transactions, nominations, allocations, intl} = this.props
    const equityDeal = dealSheet.typeOfIssuance === 'equity'
    return (
      <div className={styles.details}>
        <Typography variant='h6'>
          <FormattedMessage id="allocations.title" />
        </Typography>
        <Table className={[tableStyles.secondary, styles.table].join(' ')} breakpoint={800}>
          <TableHead>
            <TableRow>
              <TableCell><FormattedMessage id="allocations.table.heading.start_date" /></TableCell>
              <TableCell><FormattedMessage id="allocations.table.heading.total_amount" /></TableCell>
              {!equityDeal && <Fragment>
                <TableCell><FormattedMessage id="allocations.table.heading.filled_amount" /></TableCell>
                <TableCell><FormattedMessage id="allocations.table.heading.rate" /></TableCell>
              </Fragment>}
              <TableCell><FormattedMessage id="allocations.table.heading.status" /></TableCell>
              <TableCell></TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {nominations.map(nomination =>
              <TableRow key={`nomination-${nomination.id}`} className={styles.pending}>
                <TableCell mobileLabel="Time"><DateTimeDisplay value={nomination.updatedAt} singleLine /></TableCell>                
                <TableCell mobileLabel="Allocation">
                  {nomination.value ? (
                    `${nomination.value} ${this.props.dealSheet.currency.code}`
                  ) : (
                    `${nomination.amount} ${this.props.dealSheet.ticker || 'Unit'}`
                  )}
                </TableCell>
                {!equityDeal && <Fragment>
                  <TableCell mobileLabel="Filled">-</TableCell>
                  <TableCell mobileLabel="Price">-</TableCell>
                </Fragment>}
                <TableCell mobileLabel="Status" className={styles.fillStatus}>
                  <FormattedMessage id="deal.allocation_status.pending" />
                </TableCell>
                <TableCell></TableCell>
              </TableRow>
            )}
            {allocations.map(allocation =>
              <TableRow key={`allocation-${allocation.id}`} className={styles[this.allocationState(allocation)]}>
                <TableCell mobileLabel={intl.formatMessage({id: 'allocations.table.heading.start_date'})}><DateTimeDisplay type="date" value={allocation.startDate} singleLine /></TableCell>
                <TableCell mobileLabel={intl.formatMessage({id: 'allocations.table.heading.total_amount'})}><NumberDisplay>{+allocation.amount}</NumberDisplay> {this.ticker}</TableCell>
                {!equityDeal && <Fragment>
                  <TableCell mobileLabel={intl.formatMessage({id: 'allocations.table.heading.filled_amount'})}><NumberDisplay>{+allocation.amountClaimed}</NumberDisplay> {this.ticker}</TableCell>
                  <TableCell mobileLabel={intl.formatMessage({id: 'allocations.table.heading.rate'})}>{allocation.rate} {this.ticker} / {dealSheet.currency.code}</TableCell>
                </Fragment>}
                <TableCell mobileLabel={intl.formatMessage({id: 'allocations.table.heading.status'})} className={styles.fillStatus}>
                  <FormattedMessage id={`deal.allocation_status.${this.allocationState(allocation)}`} />
                </TableCell>
                <TableCell>{this.allocationState(allocation) === 'incomplete' && <Button variant="contained" color="primary" onClick={this.handleContribute(allocation)} className={styles.allocationButton}>Pay</Button>}</TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
        <Typography variant='h6'>Transactions</Typography>
        <Table className={[tableStyles.secondary, styles.table].join(' ')} breakpoint={800}>
          <TableHead>
            <TableRow>
              <TableCell>
                <FormattedMessage id="transactions.table.heading.confirmed_at" />
              </TableCell>
              <TableCell>
                <FormattedMessage id="transactions.table.heading.id" />
              </TableCell>
              <TableCell>
                <FormattedMessage id="transactions.table.heading.value" />
              </TableCell>
              {!equityDeal && (
                <TableCell>
                  <FormattedMessage id="transactions.table.heading.amount" />
                </TableCell>
              )}
            </TableRow>
          </TableHead>
          <TableBody>
            {transactions.map(transaction =>
              <TableRow key={transaction.id}>
                <TableCell mobileLabel={intl.formatMessage({id: 'transactions.table.heading.confirmed_at'})}><DateTimeDisplay value={transaction.transactionConfirmedAt} singleLine /></TableCell>
                <TableCell mobileLabel={intl.formatMessage({id: 'transactions.table.heading.id'})}><span className={styles.ellipsis}>{transaction.paymentIdentifier}</span></TableCell>
                <TableCell mobileLabel={intl.formatMessage({id: 'transactions.table.heading.value'})}><NumberDisplay>{+transaction.amount}</NumberDisplay> {dealSheet.currency.code}</TableCell>
                {!equityDeal && <TableCell mobileLabel={intl.formatMessage({id: 'transactions.table.heading.amount'})}><NumberDisplay>{+transaction.tokensPurchased}</NumberDisplay> {this.ticker}</TableCell> }
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>
    )
  }

}

export default withRouter(injectIntl(AllocationDetails))

const styles = StyleSheet({
  details: {
    '& > h2': {
      paddingTop: 25,
      borderTop: 'solid 1px #cfcfcf'
    }
  },
  incomplete: {
    '& strong': {
      color: '#90c814'
    },
    '& $fillStatus,$timeStatus': {
      color: '#6AAEAA'
    }
  },
  complete: {
    '& strong,$fillStatus': {
      color: '#90c814'
    },
  },
  pending: {
    '& strong,$fillStatus': {
      color: '#f5bd23'
    },
  },
  saft_pending: {
    '& strong,$fillStatus': {
      color: '#f5bd23'
    },
  },
  upcoming: {
    '& strong': {
      color: '#90c814'
    },
    '& $fillStatus,$timeStatus': {
      color: '#f5bd23'
    },
  },
  allocationSummary: {
    display: 'flex',
    flexWrap: 'wrap',
    alignItems: 'baseline',
    marginBottom: 15,
    paddingLeft: 5,
    '& > dt': {
      paddingRight: 6,
      paddingBottom: 5,
      '&:not(:first-of-type)': {
        marginRight: 100,
        '@media(max-width: 600px)': {
          fontWeight: 'normal',
        },
      },
      '&::after': {
        content: "':'"
      },
      '&:first-of-type': {
        fontSize: 16,
        '@media(max-width: 600px)': {
          fontSize: 14,
        },
        fontWeight: 'bold',
        paddingBottom: 15,
      },
    },
    '& > dd': {
      paddingRight: 20,
      margin: 0,
      fontWeight: 'bold',
      '&:first-of-type': {
        flexGrow: 1,
        fontSize: 16,
        '@media(max-width: 600px)': {
          fontSize: 14,
        },
        paddingBottom: 15,
      },
      '&:not(:first-of-type)': {
        marginLeft: -100,
      }
    },

  },
  fillStatus: {},
  timeStatus: {},
  allocationButton: {
    minHeight: 0,
    paddingTop: 6,
    paddingBottom: 4,
    fontSize: 11,
  },
  table: {
    margin: '10px 0',
    '& td:first-child, th:first-child': {
      paddingLeft: '5px !important',
    },
    '& td:last-child, th:last-child': {
      paddingRight: '5px !important',
    },
    '&.collapsed': {
      '& $allocationButton': {
        width: '100%',
        margin: '3px 0'
      },
      '& tr': {
        paddingLeft: '0 !important',
        paddingRight: '0 !important',
      },
      '& td': {
        paddingLeft: '5px !important',
        paddingRight: '5px !important',
      }
    }
  },
  ellipsis: {
    textOverflow: 'ellipsis',
    '@media(max-width: 600px)': {
      maxWidth: '200px',
    },
    overflow: 'hidden',
    display: 'inline-block',
    verticalAlign: 'bottom'
  },
})